// Per-turn timing derivation (fn-49.3, R4) — the ACTIVE-SEGMENT model.
//
// A PURE helper over `StreamState.events` (already chronologically
// merged + deduped across seed/live — reconnect-safe by construction;
// see .flow memory: seedlive-merge-in-react-hooks-must-2026-05-14).
// NO reducer state backs this: deriving spans from the deduped event
// log has zero new state-machine surface.
//
// **The model** (fn-49 plan-reviewed, contradiction-free):
//   - An ACTIVE SEGMENT starts at a `user.message` OR a HITL ack
//     (`user.tool_confirmation` / `user.custom_tool_result`) and ends
//     at the next `session.status_idle` event.
//   - If that idle's `stop_reason.type === 'requires_action'`, the
//     TURN CONTINUES — the idle opens a HITL wait that the next ack
//     closes (starting the turn's next active segment).
//   - Any other idle (`end_turn`, etc. — stop_reason rides ON
//     status_idle) COMPLETES the turn.
//   - Turn duration = Σ active-segment durations. HITL waits are
//     excluded BY CONSTRUCTION (each gate individually — lawyer review
//     gaps can be hours; multi-gate turns supported).
//
// **Timestamps:** `event.createdAt` only — two SERVER stamps per
// segment are clock-skew-free; client `Date.now()` never mixes in.
// A missing BOUNDARY stamp (a segment's starting user-message/ack or
// its ending idle) makes the whole turn's duration uncomputable — the
// turn is OMITTED from the result (never NaN). Unstamped MIDDLE events
// don't suppress anything; only boundaries are read.
//
// **Attachment rule (explicit):** each timing carries BOTH the
// completing-idle event id AND `attachToEventId` — the last RENDERABLE
// event of the turn (last user/agent message or tool event before the
// completing idle) — so `buildChatItems` attaches the duration to an
// item it actually renders. No implicit scan logic in the projection.
/** Segment-starting HITL acks (close a requires_action wait). */
function isAck(type) {
    return type === 'user.tool_confirmation' || type === 'user.custom_tool_result';
}
/**
 * Events the chat projection renders as (or folds onto) a visible
 * item, eligible as duration attach targets:
 *   - `user.message` / `agent.message` → bubbles
 *   - `agent.[custom_]tool_use` → activity steps
 *   - `agent.tool_result` / `user.custom_tool_result` → bundled onto
 *     their step as `result.id`
 * `user.tool_confirmation` is EXCLUDED: it folds as a decision patch
 * whose event id the projection does not retain, so it can never be
 * matched back to a rendered item.
 */
const RENDERABLE_TYPES = new Set([
    'user.message',
    'agent.message',
    'agent.tool_use',
    'agent.custom_tool_use',
    'agent.tool_result',
    'user.custom_tool_result',
]);
/** Parse an ISO stamp to epoch ms; null when absent/invalid. */
function parseMs(iso) {
    if (iso === undefined)
        return null;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
}
/** Read `stop_reason.type` off a status_idle payload. */
function stopReasonType(ev) {
    const sr = ev.data.stop_reason;
    if (sr === null || typeof sr !== 'object')
        return undefined;
    const t = sr.type;
    return typeof t === 'string' ? t : undefined;
}
/**
 * Derive per-turn timings from the (chronological) event log. Only
 * COMPLETED turns with all boundary stamps present produce an entry —
 * the in-flight turn never does (its working indicator covers it),
 * and a missing boundary stamp omits the turn entirely.
 *
 * Defensive re-dedupe by id: `StreamState.events` is already deduped,
 * but raw replayed arrays (tests, future callers) stay safe.
 */
export function computeTurnTimings(events) {
    const out = [];
    const seen = new Set();
    let turnOpen = false;
    /** All boundary stamps observed so far on the open turn. */
    let boundariesStamped = true;
    /**
     * A requires_action idle closed a segment and we have NOT yet seen
     * the ack that starts the next one. Live MA delivers ack events
     * UNSTAMPED, which sorts them to the array tail — so the completing
     * idle can arrive in array order with the gate still "awaiting".
     * Emitting then would show only the pre-gate segment (the exact
     * misleading number the live e2e caught). An unresolved gate at
     * completion ⇒ the turn's duration is unverifiable ⇒ omit.
     */
    let awaitingAck = false;
    /**
     * A gated turn just completed UNRESOLVED (its idle arrived while
     * awaitingAck — the unstamped ack sorts to the tail). That late ack
     * belongs to the already-dropped turn: consuming it as a fresh
     * segment start would open phantom timing state and poison the NEXT
     * real turn's measurement. Discard exactly one ack after such a
     * drop.
     */
    let discardNextAck = false;
    let sumMs = 0;
    let segmentOpen = false;
    let segmentStartMs = null;
    let lastRenderableId = null;
    for (const ev of events) {
        if (seen.has(ev.id))
            continue;
        seen.add(ev.id);
        // Segment start: user.message opens a turn; an ack closes a HITL
        // wait on the open turn (or, defensively, opens one mid-history —
        // a partial seed can begin inside a gated turn).
        if (!segmentOpen && (ev.type === 'user.message' || isAck(ev.type))) {
            if (isAck(ev.type) && discardNextAck) {
                // Tail-ordered ack of an already-omitted gated turn — spent.
                discardNextAck = false;
                continue;
            }
            // A fresh user message supersedes any pending late-ack discard:
            // the dropped turn's ack can no longer be confused with it.
            if (ev.type === 'user.message')
                discardNextAck = false;
            turnOpen = true;
            segmentOpen = true;
            if (isAck(ev.type))
                awaitingAck = false;
            segmentStartMs = parseMs(ev.createdAt);
            if (segmentStartMs === null)
                boundariesStamped = false;
        }
        if (turnOpen && RENDERABLE_TYPES.has(ev.type))
            lastRenderableId = ev.id;
        if (ev.type === 'session.status_idle' && turnOpen) {
            if (segmentOpen) {
                const endMs = parseMs(ev.createdAt);
                if (segmentStartMs !== null && endMs !== null) {
                    sumMs += Math.max(0, endMs - segmentStartMs);
                }
                else {
                    boundariesStamped = false;
                }
                segmentOpen = false;
                segmentStartMs = null;
            }
            if (stopReasonType(ev) !== 'requires_action') {
                // Turn completes. Emit only when every boundary was stamped,
                // every HITL gate's ack segment was actually observed, and
                // something renderable exists to attach to.
                if (boundariesStamped && !awaitingAck && lastRenderableId !== null) {
                    out.push({
                        completingIdleEventId: ev.id,
                        attachToEventId: lastRenderableId,
                        durationMs: sumMs,
                    });
                }
                // Unresolved gate at completion → the ack is still in flight
                // (tail-ordered); make sure it can't seed phantom state.
                if (awaitingAck)
                    discardNextAck = true;
                turnOpen = false;
                boundariesStamped = true;
                awaitingAck = false;
                sumMs = 0;
                lastRenderableId = null;
            }
            else {
                // requires_action: the turn stays open, waiting for its ack.
                awaitingAck = true;
            }
        }
    }
    return out;
}
//# sourceMappingURL=turn-timings.js.map